import type {Editor} from "@tiptap/react";
import {createContext, useContext, useState, type Dispatch, type ReactNode, type SetStateAction} from "react";

type SetActiveEditor = Dispatch<SetStateAction<Editor | null>>;

const ActiveEditorContext = createContext<Editor | null>(null);

// Its own context: the setter never changes, so the fields that report focus never re-render for it.
const SetActiveEditorContext = createContext<SetActiveEditor | null>(null);

/**
 * Which rich text field the author worked in last, for a side panel that follows it (the survey editor's
 * Variable section). Focus decides, not selection: a field keeps its selected chip after it blurs, and the
 * panel's own inputs are where focus goes next.
 */
export function RichTextActiveEditorProvider({children}: {children: ReactNode}) {
    const [editor, setEditor] = useState<Editor | null>(null);

    return (
        <SetActiveEditorContext.Provider value={setEditor}>
            <ActiveEditorContext.Provider value={editor}>{children}</ActiveEditorContext.Provider>
        </SetActiveEditorContext.Provider>
    );
}

/** The field the author focused last; null before any, and outside a provider. */
export const useRichTextActiveEditor = () => useContext(ActiveEditorContext);

/** For `RichTextField` only. Null outside a provider: a field elsewhere has no side panel to report to. */
export const useSetRichTextActiveEditor = () => useContext(SetActiveEditorContext);
